import { useQuery, useMutation } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { useDispatch } from '@tabeeb/store'

import { ArticlesQueryKeys } from '../queries'
import { AnnouncementPlatform } from '../enums'

export interface UseUnreadAnnouncementsProps {
  onError?: (error: unknown) => void
}

export const useUnreadAnnouncements = ({ onError }: UseUnreadAnnouncementsProps = {}) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [ArticlesQueryKeys.UnreadAnnouncements],
    queryFn: () => api.announcements.getUnreadAsync({ platform: AnnouncementPlatform.Web }),
    enabled: true,
    onError,
  })

  const markAsReadMutation = useMutation({
    mutationFn: () => {
      const ids = (query.data ?? []).map((a) => a.Id)
      return api.announcements.markAsReadAsync({ AnnouncementIds: ids })
    },
    onSuccess: () => query.refetch(),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to mark announcements as read' }))
    },
  })

  return {
    loading: query.isLoading,
    announcements: query.data ?? [],
    hasUnviewedAnnouncements: !!query.data && query.data.length > 0,
    onMarkAsRead: markAsReadMutation.mutateAsync,
    isSubmitInProgress: markAsReadMutation.isPending,
    onReload: query.refetch,
  }
}

export default useUnreadAnnouncements
